import { fabric } from 'fabric';
import type { Resolution } from 'modules/fabric-editor';
import type { LogoData, LogoSlot, LogoVariant } from './types';

/** Slot ordering for the inspector's 3×3 mini-field (center is intentionally absent). */
export const LOGO_SLOTS: LogoSlot[] = [
  'top-left',
  'top-center',
  'top-right',
  'middle-left',
  'middle-right',
  'bottom-left',
  'bottom-center',
  'bottom-right',
];

export const DEFAULT_LOGO_SLOT: LogoSlot = 'bottom-right';
/** Default size multiplier (1 = 100% of the variant's nominal design size). */
export const DEFAULT_LOGO_SCALE = 1;

// Minimum size multiplier per variant — a floor, not a cap: authors may scale
// the logo arbitrarily large.
const MIN_SCALE_BY_VARIANT: Record<LogoVariant, number> = {
  square: 0.8,
  horizontal: 0.8,
  vertical: 0.8,
};

export const DEFAULT_LOGO_MARGIN = 32;
export const MIN_LOGO_MARGIN = 0;
export const MAX_LOGO_MARGIN = 200;

const SLOT_VARIANT: Record<LogoSlot, LogoVariant> = {
  'top-left': 'square',
  'top-right': 'square',
  'bottom-left': 'square',
  'bottom-right': 'square',
  'top-center': 'horizontal',
  'bottom-center': 'horizontal',
  'middle-left': 'vertical',
  'middle-right': 'vertical',
};

export function variantForSlot(slot: LogoSlot): LogoVariant {
  return SLOT_VARIANT[slot];
}

export function scaleBoundsForVariant(variant: LogoVariant): { min: number } {
  return { min: MIN_SCALE_BY_VARIANT[variant] };
}

export function clampLogoScale(scale: number, variant: LogoVariant): number {
  const { min } = scaleBoundsForVariant(variant);
  if (!Number.isFinite(scale)) return DEFAULT_LOGO_SCALE;
  return Math.max(min, scale);
}

export function clampLogoMargin(margin: number): number {
  if (!Number.isFinite(margin)) return DEFAULT_LOGO_MARGIN;
  return Math.max(MIN_LOGO_MARGIN, Math.min(MAX_LOGO_MARGIN, Math.round(margin)));
}

// Brand-mark asset per variant, served from the public folder (same convention
// as `shared/constants/links.ts`'s `/images/*`).
const LOGO_ASSET_SRC: Record<LogoVariant, string> = {
  square: '/images/logo-square.svg',
  horizontal: '/images/logo-horizontal.svg',
  vertical: '/images/logo-vertical.svg',
};

// Intrinsic (nominal, scale = 1) pixel size of each SVG asset — mirrors the
// `width`/`height` declared on the root `<svg>` in `public/images/logo-*.svg`.
// Known statically so the backing plate can be sized correctly even before
// the raster element has finished loading.
const ASSET_SIZE: Record<LogoVariant, { width: number; height: number }> = {
  square: { width: 66, height: 66 },
  horizontal: { width: 133, height: 29 },
  vertical: { width: 29, height: 133 },
};

const BG_RADIUS_BY_VARIANT: Record<LogoVariant, number> = {
  square: 18,
  horizontal: 22,
  vertical: 22,
};

/**
 * Gap between the backing plate's edge and the logo asset, in px (nominal,
 * scale = 1). Pulled out on its own so it's a one-line tweak, per request.
 */
export const LOGO_ASSET_PADDING = 16;

const BG_COLOR = '#151a2d';

// Raster element cache, keyed by variant — populated by `preloadLogoAssets()`.
// `createLogoGroup` stays synchronous (it's called inside
// `editor.history.transaction()`, which only supports synchronous callbacks),
// so it reads from this cache rather than awaiting the load itself.
const assetElementCache = new Map<LogoVariant, HTMLImageElement>();

function loadAssetElement(variant: LogoVariant): Promise<HTMLImageElement | undefined> {
  const cached = assetElementCache.get(variant);
  if (cached) return Promise.resolve(cached);
  return new Promise((resolve) => {
    fabric.Image.fromURL(
      LOGO_ASSET_SRC[variant],
      (img) => {
        const element = img?.getElement?.() as HTMLImageElement | undefined;
        if (element) assetElementCache.set(variant, element);
        resolve(element);
      },
      { crossOrigin: 'anonymous' },
    );
  });
}

let preloadPromise: Promise<void> | undefined;

/**
 * Warms the brand-mark raster cache so `createLogoGroup` can attach the icon
 * synchronously. The live editor fires this eagerly below (module load happens
 * well before any user interacts with the logo picker); the headless render
 * pipeline awaits it explicitly (`ensureMandatoryLogo`) so a cold render page
 * is never caught racing the fetch.
 */
export function preloadLogoAssets(): Promise<void> {
  if (!preloadPromise) {
    const variants = Object.keys(LOGO_ASSET_SRC) as LogoVariant[];
    preloadPromise = Promise.all(variants.map(loadAssetElement)).then(() => undefined);
  }
  return preloadPromise;
}

preloadLogoAssets();

// Locked, inert flags — mirror the grid group so the logo can't be selected,
// moved, scaled, or deleted directly. Positioning is inspector-only (8 slots).
const LOCK_FLAGS = {
  selectable: false,
  evented: false,
  hoverCursor: 'default',
  lockMovementX: true,
  lockMovementY: true,
  lockRotation: true,
  lockScalingX: true,
  lockScalingY: true,
  lockUniScaling: true,
  hasControls: false,
  objectCaching: false,
  subTargetCheck: false,
} as const;

export interface Point {
  left: number;
  top: number;
}

/** Top-left corner for a slot, insetting `margin` px from the canvas edges. */
export function slotToPosition(slot: LogoSlot, width: number, height: number, margin: number, resolution: Resolution): Point {
  const W = Math.max(1, Math.round(resolution.width));
  const H = Math.max(1, Math.round(resolution.height));
  const isLeft = slot === 'top-left' || slot === 'middle-left' || slot === 'bottom-left';
  const isRight = slot === 'top-right' || slot === 'middle-right' || slot === 'bottom-right';
  const isTop = slot === 'top-left' || slot === 'top-center' || slot === 'top-right';
  const isBottom = slot === 'bottom-left' || slot === 'bottom-center' || slot === 'bottom-right';
  const left = isLeft ? margin : isRight ? W - width - margin : Math.round((W - width) / 2);
  const top = isTop ? margin : isBottom ? H - height - margin : Math.round((H - height) / 2);
  return { left, top };
}

export function createLogoGroup(slot: LogoSlot, scale: number, margin: number, resolution: Resolution): fabric.Group {
  const variant = variantForSlot(slot);
  const asset = ASSET_SIZE[variant];
  const s = clampLogoScale(scale, variant);
  const m = clampLogoMargin(margin);
  const padding = Math.round(LOGO_ASSET_PADDING * s);
  const assetWidth = Math.round(asset.width * s);
  const assetHeight = Math.round(asset.height * s);
  const width = assetWidth + padding * 2;
  const height = assetHeight + padding * 2;
  const radius = Math.round(BG_RADIUS_BY_VARIANT[variant] * s);

  const bg = new fabric.Rect({
    left: 0,
    top: 0,
    width,
    height,
    rx: radius,
    ry: radius,
    fill: BG_COLOR,
    strokeWidth: 0,
    selectable: false,
    evented: false,
    objectCaching: false,
  });

  const children: fabric.Object[] = [bg];

  // Only attached once the raster is warm (see `preloadLogoAssets`) — this
  // keeps `createLogoGroup` synchronous, which `editor.history.transaction()`
  // requires. In the live editor the cache is warm by the time a user can
  // reach the picker; `ensureMandatoryLogo` awaits the preload explicitly.
  const iconElement = assetElementCache.get(variant);
  if (iconElement) {
    const icon = new fabric.Image(iconElement, {
      left: width / 2,
      top: height / 2,
      originX: 'center',
      originY: 'center',
      scaleX: assetWidth / asset.width,
      scaleY: assetHeight / asset.height,
      selectable: false,
      evented: false,
      objectCaching: false,
    });
    children.push(icon);
  }

  const group = new fabric.Group(children, {
    originX: 'left',
    originY: 'top',
    ...LOCK_FLAGS,
  });
  const pos = slotToPosition(slot, width, height, m, resolution);
  group.set({ left: pos.left, top: pos.top });

  // Materialize every default into `data` so it is the single source of truth
  // and round-trips through toJSON/loadFromJSON and undo/redo.
  const data: LogoData = { kind: 'logo', pin: 'top', variant, position: slot, scale: s, margin: m, isSave: true };
  group.data = data;
  return group;
}

/**
 * Re-applies the inert lock flags and re-derives the group's geometry from its
 * `data` — the single source of truth for slot / scale / margin.
 *
 * Why this is needed: Fabric's default `toObject` does not carry `selectable`,
 * `evented`, `lock*` or `hasControls`, and a template is persisted with
 * `toJSON(['data'])`. A logo restored from a saved template therefore comes
 * back fully interactive — draggable, scalable, rotatable — which is the bug
 * this closes. The history plugin does list the lock flags in its
 * `propertiesToInclude`, but it can only preserve what was on the canvas when
 * the snapshot was taken, so undo/redo/reset inherit whatever the initial load
 * produced. Every load path funnels through the logo plugin's `ensure()` (and
 * the render pipeline's `ensureMandatoryLogo`), so normalizing there covers
 * open, undo, redo and discard alike.
 *
 * Geometry is re-derived, not just re-locked: a template saved while the logo
 * was movable can carry a dragged/scaled/rotated logo, and the persisted
 * `data.position` must win over the stale `left`/`top`.
 */
export function normalizeLogoGroup(group: fabric.Group, resolution: Resolution): void {
  const data = getLogoData(group);
  if (!data) return;

  const slot = data.position ?? DEFAULT_LOGO_SLOT;
  const variant = variantForSlot(slot);
  const scale = clampLogoScale(data.scale ?? DEFAULT_LOGO_SCALE, variant);
  const margin = clampLogoMargin(data.margin ?? DEFAULT_LOGO_MARGIN);

  group.set({
    ...LOCK_FLAGS,
    originX: 'left',
    originY: 'top',
    angle: 0,
    scaleX: 1,
    scaleY: 1,
    skewX: 0,
    skewY: 0,
    flipX: false,
    flipY: false,
  });

  const pos = slotToPosition(slot, group.width ?? 0, group.height ?? 0, margin, resolution);
  group.set({ left: pos.left, top: pos.top });
  group.setCoords();

  // Materialize the (possibly clamped / missing) values back into `data` so the
  // inspector, the visual and the next save all agree — including on legacy
  // logos stored before `pin` / `margin` existed.
  const normalized: LogoData = { kind: 'logo', pin: 'top', variant, position: slot, scale, margin, isSave: true };
  group.data = normalized;
}

export function getLogoData(obj: fabric.Object | undefined | null): LogoData | undefined {
  const data = obj?.data as { kind?: unknown } | undefined;
  if (!data || data.kind !== 'logo') return undefined;
  return obj!.data as LogoData;
}

export function findLogo(canvas: fabric.Canvas): fabric.Group | undefined {
  return canvas.getObjects().find((o) => getLogoData(o) !== undefined) as fabric.Group | undefined;
}
